import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconMessagePlus } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { createNote, deleteNote, fetchNotes, patchNote } from '@/reducers/notes/notes.actions'
import { anchorKey } from '@/reducers/notes/notes.redux'
import { Button } from '@/components/buttons/Button'

import { allowAgentVisibleFor } from './noteVisibility'
import { NoteCard } from './NoteCard'
import { NoteForm } from './NoteForm'
import { useOrgUsers } from './useOrgUsers'

import './NotesPanel.scss'

/**
 * The shared single-anchor notes surface: scorecard note (CallQAScores),
 * criteria note (QAScoreEditCriteria), the inbox task drawer (TaskComments),
 * and the playlist comment editor. The Call Explorer Comments tab uses
 * CallNotesTab instead, which aggregates every note across a call.
 *
 * @param {object} props
 * @param {{noteType: string, id: number}} props.anchor
 * @param {number} props.organizationId
 * @param {string} [props.title] - header label
 * @param {Array<{value: number, label: string}>} [props.criteriaOptions] - only for call notes wanting to pre-link a criterion at create time
 * @param {number} [props.callMomentTimestamp] - pre-fill for call notes anchored to a transcript timestamp
 * @param {boolean} [props.notifyAgentEnabled=false] - gates the visual checkbox until TREN-271 ships the backend
 * @param {boolean} [props.editingDisabled=false]
 * @param {boolean} [props.compact=false] - drops the section header (e.g. when embedded inside a criteria card)
 * @param {boolean} [props.collapsible=true] - hide the compose form behind an "Add a note" trigger. Pass false when the caller already gates the panel behind its own toggle (e.g. the criteria "Add a note" button), so the form shows directly instead of a second click.
 * @param {string} [props.placeholder] - compose textarea placeholder override
 * @param {boolean} [props.saveOnUnmount=false] - auto-save an in-progress draft when the compose form unmounts (task drawers)
 * @param {() => void} [props.onChange] - called after a create/edit/delete settles, so a caller can refresh derived state (e.g. the playlist table's comment count)
 */
export const NotesPanel = ({
  anchor,
  organizationId,
  title,
  criteriaOptions,
  callMomentTimestamp,
  notifyAgentEnabled = false,
  editingDisabled = false,
  compact = false,
  collapsible = true,
  placeholder,
  saveOnUnmount = false,
  onChange,
}) => {
  const dispatch = useDispatch()
  const key = anchorKey(anchor.noteType, anchor.id)
  const notes = useSelector((state) => state.notes.byAnchor[key] || [])
  const isLoading = useSelector((state) => state.notes.loading[key] === true)
  const orgUsers = useOrgUsers(organizationId)
  const anchorReady = anchor.id != null
  // Compose form stays collapsed behind an "Add a note" trigger so the
  // embedded surfaces (scorecard / criteria cards) aren't dominated by a
  // textarea that's usually empty.
  const [isComposing, setIsComposing] = useState(false)

  useEffect(() => {
    if (!anchorReady) {
      return
    }
    dispatch(fetchNotes({ noteType: anchor.noteType, id: anchor.id, organizationId }))
  }, [anchorReady, anchor.noteType, anchor.id, organizationId, dispatch])

  const handleCreate = async ({ noteText, visibility, mentionedUserIds, criteriaScoreId }) => {
    await dispatch(
      createNote({
        noteType: anchor.noteType,
        id: anchor.id,
        noteText,
        visibility,
        mentionedUserIds,
        callMomentTimestamp,
        criteriaScoreId,
        organizationId,
      })
    )
    setIsComposing(false)
    onChange?.()
  }

  const handlePatch = async (noteId, fields) => {
    await dispatch(patchNote({ noteId, fields, anchor, organizationId }))
    onChange?.()
  }

  const handleDelete = async (noteId) => {
    await dispatch(deleteNote({ noteId, anchor, organizationId }))
    onChange?.()
  }

  const visibleNotes = notes.filter((note) => !note.deleted)
  const showAgentVisibleOption = allowAgentVisibleFor(anchor.noteType)

  return (
    // Intentionally a <div>, not a <section>: some embedding surfaces
    // (e.g. CallQAScores' .qa-score card) style bare `section`/`header`
    // elements, and those rules would otherwise bleed into the panel and
    // recenter the form. See NoteCard for the matching header note.
    <div
      className={classNames('notes-panel', { 'is-compact': compact })}
      data-testid={`notes-panel-${anchor.noteType}-${anchor.id}`}
    >
      {!compact && title && <h3 className="notes-panel__title">{title}</h3>}

      {/* Compose first — it never depends on the notes fetch, so it's stable
          and the spinner/notes below don't shove it around. */}
      {/* Non-collapsible: caller already gated us behind its own toggle, so
          show the form directly (no second click). */}
      {!collapsible && !editingDisabled && anchorReady && (
        <NoteForm
          organizationId={organizationId}
          orgUsers={orgUsers}
          mode="create"
          criteriaOptions={criteriaOptions}
          allowAgentVisible={showAgentVisibleOption}
          notifyAgentEnabled={notifyAgentEnabled}
          placeholder={placeholder}
          saveOnUnmount={saveOnUnmount}
          onSubmit={handleCreate}
        />
      )}

      {/* Collapsible: compose stays behind an "Add a note" trigger. */}
      {collapsible && !editingDisabled && anchorReady && isComposing && (
        <NoteForm
          organizationId={organizationId}
          orgUsers={orgUsers}
          mode="create"
          autoFocus
          criteriaOptions={criteriaOptions}
          allowAgentVisible={showAgentVisibleOption}
          notifyAgentEnabled={notifyAgentEnabled}
          placeholder={placeholder}
          saveOnUnmount={saveOnUnmount}
          onSubmit={handleCreate}
          onCancel={() => setIsComposing(false)}
        />
      )}

      {collapsible && !editingDisabled && anchorReady && !isComposing && (
        <Button
          square
          className="notes-panel__add"
          data-testid={`notes-panel-add-${anchor.noteType}-${anchor.id}`}
          onClick={() => setIsComposing(true)}
          tooltip="Add a note"
        >
          <IconMessagePlus />
        </Button>
      )}

      {/* Notes below the compose box. Spinner only on first load (no cached
          notes yet); subsequent opens read from redux and skip it. */}
      {isLoading && isEmpty(visibleNotes) && (
        <div className="notes-panel__loading">
          <Loader size="small" />
        </div>
      )}

      {!isEmpty(visibleNotes) && (
        <div className="notes-panel__list">
          {visibleNotes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              organizationId={organizationId}
              orgUsers={orgUsers}
              onPatch={handlePatch}
              onDelete={handleDelete}
              editingDisabled={editingDisabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}
